'use client'

/**
 * Navigation Hydration Hook
 *
 * Handles loading persisted navigation state from localStorage
 * and initializing the Redux store on app startup.
 */

import { useEffect, useLayoutEffect, useState } from 'react';
import { useAppDispatch } from '@/shared/providers/hooks';
import { hydrateNavigation, loadNavigationState, setMainSidebarCollapsed, toggleMainSidebar } from './navigationSlice';
import {
  isTradingDeskSidebarPath,
  tradingDeskForcedStart,
  tradingDeskForcesCollapse,
  unforcedSidebarCollapsed,
} from '@/shared/utils/sidebar/previewSidebar';

// Layout effect on the client (no localStorage on the server): the saved setting lands
// before any child's passive effect, so page-level forced collapses (TradingPage <= 1440px,
// the /trading/preview default) still run after it and win, as before.
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Hook to hydrate navigation state from localStorage
 * Call this in your app's root component or layout
 */
export const useNavigationHydration = (pathname?: string) => {
  const dispatch = useAppDispatch();

  useClientLayoutEffect(() => {
    // Load persisted state from localStorage
    const persistedState = loadNavigationState();
    const path = pathname ?? window.location.pathname;

    // /trading and /trading/preview: apply the forced start here, in the same layout
    // phase that reads the saved setting, so the first client paint is already 64px
    // (no 300 -> 64 snap). hydrateNavigation is never persisted.
    if (isTradingDeskSidebarPath(path) && tradingDeskForcedStart(path, window.innerWidth, persistedState)) {
      const collapsed = deskVisitChoiceFor(path) ?? true;
      dispatch(hydrateNavigation({ mainSidebarCollapsed: collapsed, navbarHeaderVisible: !collapsed }));
      return;
    }

    if (Object.keys(persistedState).length > 0) {
      dispatch(hydrateNavigation(persistedState));
    }
  }, [dispatch, pathname]);
};

/**
 * Sidebar client state: `clientReady` flips in a layout effect (before the first client
 * paint, same phase as the hydration step), `transitions` two frames later, after the
 * post-load width has been painted. Until then the rail renders with no transition.
 */
export const useSidebarClientState = () => {
  const [clientReady, setClientReady] = useState(false);
  const [transitions, setTransitions] = useState(false);
  useClientLayoutEffect(() => {
    setClientReady(true);
  }, []);
  useEffect(() => {
    if (!clientReady) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setTransitions(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [clientReady]);
  return { clientReady, transitions };
};

/**
 * Visit-only manual rail choice on a forced start (in memory, never persisted). It lives
 * for one visit of one desk route: a remount of the page on the same route (e.g. when the
 * ticket finishes loading) re-applies it instead of forcing collapsed again. Cleared when
 * the route changes (Sidebar), when the viewport crosses the 1440px band, and on reload.
 */
let deskVisitChoice: { path: string; collapsed: boolean } | null = null;

const deskVisitChoiceFor = (path: string | null | undefined): boolean | undefined =>
  deskVisitChoice && deskVisitChoice.path === path ? deskVisitChoice.collapsed : undefined;

/** Called by the Sidebar when its route changes (and on mount): a new visit starts forced again. */
export const startDeskRailVisit = (): void => {
  deskVisitChoice = null;
};

/**
 * The Sidebar's expand/collapse button. On a forced start (see tradingDeskForcedStart)
 * the toggle is visit-only: hydrateNavigation, which the persistence listener ignores,
 * so mainSidebarCollapsed is never written and the next load starts collapsed again.
 * Everywhere else it is the user's saved toggle, as before.
 */
export const sidebarToggleAction = (
  pathname: string | null | undefined,
  viewportWidth: number,
  currentCollapsed: boolean,
) => {
  if (isTradingDeskSidebarPath(pathname) && tradingDeskForcedStart(pathname, viewportWidth, loadNavigationState())) {
    const next = !currentCollapsed;
    deskVisitChoice = { path: pathname as string, collapsed: next };
    return hydrateNavigation({ mainSidebarCollapsed: next, navbarHeaderVisible: !next });
  }
  return toggleMainSidebar();
};

/**
 * Sidebar rules for /trading and /trading/preview (Elena, Oct 2), shared by TradingPage
 * and TradeDeskPreviewShell:
 * - At a viewport width of 1440px and below the rail is forced collapsed.
 * - Above 1440px it follows the saved setting (navigation-store); with nothing saved the
 *   preview opens collapsed and /trading expanded.
 * - Forcing goes through setMainSidebarCollapsed / hydrateNavigation, which the
 *   persistence listener ignores, so it never overwrites the saved setting. Resizing
 *   above 1440px and leaving the page bring the saved setting back (expanded if none).
 * - Forcing only sets the starting state (Elena, Oct 2, final): a manual toggle on a
 *   forced start is visit-only (sidebarToggleAction) and the next load starts collapsed.
 * Layout effect, so a client-side navigation onto these routes paints 64px from the first
 * frame; on a full load useNavigationHydration applies the same forced start (same result).
 */
export const useTradingDeskRail = (pathname: '/trading' | '/trading/preview') => {
  const dispatch = useAppDispatch();

  useClientLayoutEffect(() => {
    let forced = false;
    let changed = false;
    const restore = (path: string | null) => {
      const saved = loadNavigationState();
      const collapsed = unforcedSidebarCollapsed(saved, path);
      dispatch(
        hydrateNavigation({
          mainSidebarCollapsed: collapsed,
          navbarHeaderVisible: saved.navbarHeaderVisible ?? !collapsed,
        }),
      );
    };
    // Forcing runs only at mount and when the viewport crosses the 1440px band, never
    // after a user toggle within the same visit (no pull back to 64px).
    const sync = (initial: boolean) => {
      if (tradingDeskForcesCollapse(pathname, window.innerWidth)) {
        if (!forced) {
          const choice = initial ? deskVisitChoiceFor(pathname) : undefined;
          if (!initial) deskVisitChoice = null;
          if (choice === undefined) dispatch(setMainSidebarCollapsed(true));
          else dispatch(hydrateNavigation({ mainSidebarCollapsed: choice, navbarHeaderVisible: !choice }));
        }
        forced = true;
        changed = true;
      } else if (forced) {
        forced = false;
        deskVisitChoice = null;
        restore(pathname);
      }
    };
    const onResize = () => sync(false);
    sync(true);
    if (!forced && loadNavigationState().mainSidebarCollapsed === undefined && unforcedSidebarCollapsed({}, pathname)) {
      // Fresh preview load above 1440px with nothing saved: open collapsed (or the
      // visitor's visit-only choice when the page remounts within the same visit).
      const choice = deskVisitChoiceFor(pathname);
      if (choice === undefined) dispatch(setMainSidebarCollapsed(true));
      else dispatch(hydrateNavigation({ mainSidebarCollapsed: choice, navbarHeaderVisible: !choice }));
      changed = true;
    }
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (changed) restore(null);
    };
  }, [dispatch, pathname]);
};

export default useNavigationHydration;
